import { Check, Copy } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

/** How long the copy button shows its "copied" tick. */
const COPIED_FEEDBACK_MS = 2000;

interface CopyFieldProps {
  label: string;
  value: string;
  /** Render in a muted, secret-looking box (used for the client secret). */
  emphasised?: boolean;
  className?: string;
}

/**
 * A read-only identifier with a copy button.
 *
 * Used for the ids provisioning returns — several of them are the only copy the
 * approver will ever see, so retyping them out of the screen is not an option.
 */
export function CopyField({ label, value, emphasised = false, className }: CopyFieldProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // `writeText` rejects in a non-secure context and when clipboard permission
  // is denied. Awaited and caught, because the tick must never appear on a
  // failed copy: one of these fields is the client secret, which is shown once
  // and never again — an approver who trusts a false tick has lost it.
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    } catch (err) {
      logger.error("[app-registrations] clipboard write failed:", err);
      toast.error(
        `Couldn't copy the ${label.toLowerCase()}. Select the text and copy it manually.`,
      );
    }
  };

  return (
    <div className={className}>
      <p className="text-muted-foreground text-xs tracking-wider uppercase">{label}</p>
      <div
        className={cn(
          "border-border mt-1 flex items-center gap-2 rounded-md border px-2.5 py-1.5",
          emphasised && "bg-muted",
        )}
      >
        <code className="text-foreground min-w-0 flex-1 truncate text-xs">{value}</code>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => void handleCopy()}
          title={`Copy ${label.toLowerCase()}`}
        >
          {copied ? <Check className="text-green-500" /> : <Copy />}
          <span className="sr-only">Copy {label.toLowerCase()}</span>
        </Button>
      </div>
    </div>
  );
}
